import React from 'react';

/**
 * Top-level error boundary for Orion AI.
 * Catches rendering errors in the component tree so an AI failure
 * doesn't crash the entire shell — users see a recovery UI instead.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[Orion] Unhandled render error:', error, info.componentStack);
    // TODO: forward to error tracking — e.g. Sentry.captureException(error, { extra: info })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: '#0f172a',
            color: '#f8fafc',
            fontFamily: 'system-ui, sans-serif',
            padding: '2rem',
            textAlign: 'center',
            gap: '1rem',
          }}
        >
          <div style={{ fontSize: '2.5rem' }}>⚠️</div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
            Something went wrong
          </h1>
          <p style={{ color: '#94a3b8', margin: 0, maxWidth: 400 }}>
            An unexpected error occurred. Try refreshing the page or clicking the button below.
          </p>
          {this.state.error?.message && (
            <pre
              style={{
                background: '#1e293b',
                color: '#f87171',
                padding: '0.75rem 1rem',
                borderRadius: 6,
                fontSize: '0.8rem',
                maxWidth: 480,
                overflow: 'auto',
                textAlign: 'left',
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={this.handleReset}
            style={{
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '0.5rem 1.25rem',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600,
            }}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
